import { useCallback, useEffect, useRef, useState } from "react";
import {
  useHelpCenterStore,
  translateHelpCenterText,
} from "../../../Stores/HelpCenter";
import { useRouterStore } from "../../../Stores/Router";
import { useConfigStore } from "../../../Stores/Config";
import { ReactComponent as MagnifyingGlass } from "../../../Assets/magnifying-glass-solid.svg";
import {
  ChevronRight,
  Loading,
  NotFound,
  ArticleItem,
} from "../HelpCenterShared/HelpCenterShared";
import { AISearchResults } from "../HelpCenterAISearch/HelpCenterAISearch";
import { activateOnKey } from "../../../Helper/a11y";
import "./HelpCenterCollection.scss";

function CollectionCard({ collection }: { collection: any }) {
  return (
    <div
      className="helpcenter-collection-card"
      role="button"
      tabIndex={0}
      onClick={() => {
        useRouterStore.getState().pushPage("helpcenter-collection", {
          id: collection.docId ?? collection.id,
        });
      }}
      onKeyDown={activateOnKey(() => {
        useRouterStore.getState().pushPage("helpcenter-collection", {
          id: collection.docId ?? collection.id,
        });
      })}
    >
      {collection.iconUrl && collection.iconUrl.length > 0 ? (
        <div className="helpcenter-collection-card-container">
          <img
            src={collection.iconUrl}
            alt=""
            className="helpcenter-collection-card-container-icon"
          />
        </div>
      ) : (
        <></>
      )}
      <div className="helpcenter-collection-card-details">
        <div className="helpcenter-collection-card-details-title">
          {collection.title}
        </div>
        {collection.description && (
          <div className="helpcenter-collection-card-details-description">
            {collection.description}
          </div>
        )}
      </div>
      <ChevronRight />
    </div>
  );
}

function SearchResultsInline({
  results,
  term,
  loading,
  activeIndex = -1,
  hideEmpty,
  followedByAsk,
}: {
  results: any[];
  term: string;
  loading?: boolean;
  /** The row picked with the arrow keys (-1 = none). */
  activeIndex?: number;
  /** Kai's answer is shown above: no "nothing found" line under it. */
  hideEmpty?: boolean;
  /** The Ask row follows: it keeps the room for the tab bar, not the list. */
  followedByAsk?: boolean;
}) {
  useConfigStore(); // re-render on config (language / translations) changes
  const committedTerm = useHelpCenterStore((s) => s.searchTerm);
  const hasResults = results && results.length > 0;
  // Only true once a real search has settled for the current input (the store
  // sets searchTerm when the request fires) — so "nothing found" never flashes
  // while the user is still typing.
  const searchSettled = committedTerm === term.trim();

  return (
    <div
      className={`helpcenter-search-results${followedByAsk ? " helpcenter-search-results--before-ask" : ""}`}
    >
      {loading && !hasResults ? (
        <Loading />
      ) : hasResults ? (
        <div className="helpcenter-search-results-list" role="listbox">
          {results.map((result: any, index: number) => {
            const article = result.document ?? result;
            return (
              <ArticleItem
                key={article.id ?? article.docId}
                id={`helpcenter-search-row-${index}`}
                article={article}
                active={index === activeIndex}
              />
            );
          })}
        </div>
      ) : (
        searchSettled &&
        !hideEmpty && (
          <div className="helpcenter-search-results-empty">
            {translateHelpCenterText("noSearchResults", "nothingFoundTitle")}
          </div>
        )
      )}
    </div>
  );
}

/** The row after the results that hands the typed text to Kai (Enter does the same). */
function AskKaiRow({
  question,
  active,
  index,
  onAsk,
}: {
  question: string;
  active: boolean;
  index: number;
  onAsk: () => void;
}) {
  useConfigStore(); // re-render on config (language / translations) changes
  const label = translateHelpCenterText(
    "askAiQuestion",
    "askAiQuestion",
    "Ask AI “{question}”",
  ).replace("{question}", question);
  return (
    <div
      id={`helpcenter-search-row-${index}`}
      className={`helpcenter-search-ask${active ? " helpcenter-search-ask--active" : ""}`}
      role="button"
      tabIndex={0}
      onClick={onAsk}
      onKeyDown={activateOnKey(onAsk)}
    >
      <span className="helpcenter-search-ask-label">{label}</span>
      <span className="helpcenter-search-ask-hint">
        <kbd aria-hidden="true">↵</kbd>{" "}
        {translateHelpCenterText("shortcutAsk", "shortcutAsk", "to ask")}
      </span>
    </div>
  );
}

function SubCollectionSection({ subCollection }: { subCollection: any }) {
  const articles = subCollection.articles ?? [];
  const nestedCollections = subCollection.subCollections ?? [];

  return (
    <div className="helpcenter-collection-detail-section">
      <div className="helpcenter-collection-detail-section-header">
        {subCollection.iconUrl && subCollection.iconUrl.length > 0 && (
          <img src={subCollection.iconUrl} alt="" />
        )}
        <span
          role="button"
          tabIndex={0}
          onClick={() => {
            useRouterStore.getState().pushPage("helpcenter-collection", {
              id: subCollection.docId ?? subCollection.id,
            });
          }}
          onKeyDown={activateOnKey(() => {
            useRouterStore.getState().pushPage("helpcenter-collection", {
              id: subCollection.docId ?? subCollection.id,
            });
          })}
        >
          {subCollection.title}
        </span>
      </div>
      {articles.map((article: any) => (
        <ArticleItem key={article.id ?? article.docId} article={article} />
      ))}
      {nestedCollections.map((col: any) => (
        <ArticleItem key={col.id ?? col.docId} article={col} isCollection />
      ))}
    </div>
  );
}

// --- Exported views ---

export function CollectionsView() {
  const collections = useHelpCenterStore((s) => s.collections);
  const searchResults = useHelpCenterStore((s) => s.searchResults);
  const searchTerm = useHelpCenterStore((s) => s.searchTerm);
  const searchArticles = useHelpCenterStore((s) => s.searchArticles);
  const clearSearch = useHelpCenterStore((s) => s.clearSearch);
  const searchLoading = useHelpCenterStore((s) => s.searchLoading);
  const aiAnswer = useHelpCenterStore((s) => s.aiAnswer);
  const aiSources = useHelpCenterStore((s) => s.aiSources);
  const aiLoading = useHelpCenterStore((s) => s.aiLoading);
  const searchForAnswer = useHelpCenterStore((s) => s.searchForAnswer);
  const clearAISearch = useHelpCenterStore((s) => s.clearAISearch);
  const configStore = useConfigStore();
  // The help center's AI switch (`config.aiEnabled: false`) turns Ask off too: the API refuses the answer then.
  const helpcenterConfig = configStore.config?.helpcenterConfig;
  const isKaiAnswerEnabled = helpcenterConfig?.kaiAnswers && helpcenterConfig?.config?.aiEnabled !== false;
  // Search first: the field searches the articles; Enter (or the Ask row) asks Kai.
  const searchPlaceholder = translateHelpCenterText(
    "searchPlaceholder",
    "search",
  );
  const [inputValue, setInputValue] = useState(searchTerm);
  // The row picked with the arrow keys: the results, then the Ask row. -1 = none.
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearchChange = useCallback(
    (value: string) => {
      setInputValue(value);
      setActiveIndex(-1);
      // A new query leaves Kai's answer to the previous one behind.
      clearAISearch();

      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (!value.trim()) {
        clearSearch();
        return;
      }
      debounceRef.current = setTimeout(() => {
        searchArticles(value.trim());
      }, 300);
    },
    [searchArticles, clearSearch, clearAISearch],
  );

  const question = inputValue.trim();
  const showAskRow = Boolean(isKaiAnswerEnabled) && question.length > 0;
  const rowCount = (searchResults?.length ?? 0) + (showAskRow ? 1 : 0);
  const askRowIndex = showAskRow ? searchResults?.length ?? 0 : -1;

  const ask = () => {
    if (!question || !isKaiAnswerEnabled) return;
    setActiveIndex(-1);
    searchForAnswer(question);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!question) return;
    const hit = activeIndex >= 0 && activeIndex !== askRowIndex ? searchResults[activeIndex] : undefined;
    if (hit) {
      const article = hit.document ?? hit;
      useHelpCenterStore.getState().openArticle(article.docId ?? article.id);
      return;
    }
    ask();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (rowCount === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((index) => (index < 0 || index >= rowCount - 1 ? 0 : index + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((index) => (index <= 0 ? rowCount - 1 : index - 1));
    } else if (e.key === "Escape" && activeIndex >= 0) {
      e.preventDefault();
      setActiveIndex(-1);
    }
  };

  const handleClear = () => {
    setInputValue("");
    setActiveIndex(-1);
    clearAISearch();
    clearSearch();
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const isSearching = question.length > 0;
  const hasAIResults = Boolean(
    isKaiAnswerEnabled &&
      (aiLoading || aiAnswer || (aiSources && aiSources.length > 0)),
  );

  return (
    <>
      <form className="helpcenter-search-bar" onSubmit={handleSubmit}>
        <div className="helpcenter-search-bar-input-wrapper">
          <MagnifyingGlass />
          <input
            type="text"
            aria-label={searchPlaceholder}
            placeholder={searchPlaceholder}
            value={inputValue}
            enterKeyHint={isKaiAnswerEnabled ? "send" : "search"}
            aria-activedescendant={
              activeIndex >= 0 ? `helpcenter-search-row-${activeIndex}` : undefined
            }
            onChange={(e) => handleSearchChange(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {isSearching && (
            <div
              className="helpcenter-search-bar-clear"
              role="button"
              tabIndex={0}
              aria-label="Clear search"
              onClick={handleClear}
              onKeyDown={activateOnKey(handleClear)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </div>
          )}
        </div>
        <button
          type="submit"
          aria-label={configStore.translateText("search")}
          className="helpcenter-search-bar-submit"
          style={{
            position: "absolute",
            width: 1,
            height: 1,
            padding: 0,
            margin: -1,
            border: 0,
            overflow: "hidden",
            clip: "rect(0 0 0 0)",
            whiteSpace: "nowrap",
          }}
        />
      </form>
      {isSearching || hasAIResults ? (
        <>
          {hasAIResults && <AISearchResults />}
          {isSearching && (
            <SearchResultsInline
              results={searchResults}
              term={inputValue}
              loading={searchLoading}
              activeIndex={activeIndex}
              hideEmpty={hasAIResults}
              followedByAsk={showAskRow}
            />
          )}
          {showAskRow && (
            <AskKaiRow
              question={question}
              index={askRowIndex}
              active={activeIndex === askRowIndex}
              onAsk={ask}
            />
          )}
        </>
      ) : (
        <div className="helpcenter-collections">
          {collections.map((collection: any) => (
            <CollectionCard key={collection.id} collection={collection} />
          ))}
        </div>
      )}
    </>
  );
}

export function CollectionDetailView({
  collectionId,
}: {
  collectionId: string;
}) {
  const currentCollection = useHelpCenterStore((s) => s.currentCollection);
  const loadCollection = useHelpCenterStore((s) => s.loadCollection);

  const alreadyLoaded = (() => {
    if (!currentCollection) return false;
    const col = currentCollection.collection ?? currentCollection;
    return (col?.docId ?? col?.id) === collectionId;
  })();

  const [loading, setLoading] = useState(!alreadyLoaded);
  // Id of the collection whose load failed (404 for a deleted collection or a
  // help center the plan no longer includes), keyed like ArticleDetailView.
  const [failedCollectionId, setFailedCollectionId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (alreadyLoaded) return;
    setLoading(true);
    setFailedCollectionId((failed) =>
      failed === collectionId ? null : failed,
    );
    loadCollection(collectionId)
      .then((data) => {
        if (!data?.data) setFailedCollectionId(collectionId);
      })
      .finally(() => setLoading(false));
  }, [collectionId, loadCollection, alreadyLoaded]);

  if (loading) return <Loading />;
  // Not loaded: the empty state instead of a spinner that stays forever (and
  // instead of the previously opened collection).
  if (
    failedCollectionId === collectionId ||
    (!currentCollection?.collection && !currentCollection?.articles)
  )
    return <NotFound />;

  const collection = currentCollection.collection ?? currentCollection;
  const articles = currentCollection.articles ?? [];
  const subCollections = currentCollection.subCollections ?? [];

  return (
    <div className="helpcenter-collection-detail">
      <div className="helpcenter-collection-detail-header">
        <div
          className="helpcenter-collection-detail-header-title"
          role="heading"
          aria-level={1}
        >
          {collection.title}
        </div>
        {collection.description && (
          <div className="helpcenter-collection-detail-header-description">
            {collection.description}
          </div>
        )}
      </div>

      {articles.length > 0 && (
        <div className="helpcenter-collection-detail-section">
          {articles.map((article: any) => (
            <ArticleItem key={article.id ?? article.docId} article={article} />
          ))}
        </div>
      )}

      {subCollections.map((sub: any) => (
        <SubCollectionSection key={sub.id ?? sub.docId} subCollection={sub} />
      ))}
    </div>
  );
}
